import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { APP_CONFIG } from "../config/app-config";
import { getToken } from "../utils/authToken";
import { 
  Home, 
  Send, 
  Receipt, 
  Ticket, 
  User,
  Wifi,
  Zap,
  CreditCard,
  Download,
  QrCode,
  Banknote,
  Smartphone,
  Gift,
  Wallet,
  HelpCircle,
  MessageSquare,
  DollarSign,
  Store,
  AtSign,
  Star,
  Play,
  Tag
} from "lucide-react";

interface NavItem {
  id: string;
  path: string;
  label: string;
  navLabel?: string;
  icon: () => JSX.Element;
}

interface QuickAccessService {
  id: string;
  name: string;
  description: string;
  category: 'payment' | 'utility' | 'financial';
  available: boolean;
  comingSoon?: boolean;
}

const BOTTOM_NAV_HIDDEN_PATHS = [
  '/login',
  '/register',
  '/signup',
  '/forgot-password',
  '/change-mobile',
  '/set-app-password',
  '/verify-login-otp',
  '/kyc',
  '/kyc/documents',
  '/kyc/status',
] as const;

export function shouldShowBottomNav(pathname: string): boolean {
  if (!pathname) return false;
  if (pathname.startsWith('/register/')) return false;
  return !(BOTTOM_NAV_HIDDEN_PATHS as readonly string[]).includes(pathname);
}

export function normalizeQuickAccessServiceId(id: string): string {
  return String(id || '').trim().replace(/_/g, '-');
}

// Service mapping for quick access services - SINGLE SOURCE OF TRUTH from TransactPage
const serviceMapping = {
  'send-money': {
    id: 'send-money',
    path: '/send-money',
    label: 'Pay Someone',
    navLabel: 'Pay',
    icon: () => <Send style={{ width: '20px', height: '20px' }} />
  },
  'request-money': {
    id: 'request-money',
    path: '/request-money',
    label: 'Request Money',
    navLabel: 'Request',
    icon: () => <Download style={{ width: '20px', height: '20px' }} />
  },
  'qr-scan': {
    id: 'qr-scan',
    path: '/qr-payment',
    label: 'Scan QR to Pay',
    navLabel: 'Scan QR',
    icon: () => <QrCode style={{ width: '20px', height: '20px' }} />
  },
  'topup-easypay': {
    id: 'topup-easypay',
    path: '/topup-easypay',
    label: 'Top-up at EasyPay',
    navLabel: 'EasyPay',
    icon: () => <Wallet style={{ width: '20px', height: '20px' }} />
  },
  'airtime-data': {
    id: 'airtime-data',
    path: '/airtime-data-overlay',
    label: 'Airtime & Data',
    navLabel: 'Airtime',
    icon: () => <Smartphone style={{ width: '20px', height: '20px' }} />
  },
  'electricity': {
    id: 'electricity',
    path: '/electricity-overlay',
    label: 'Electricity & Water',
    navLabel: 'Electricity',
    icon: () => <Zap style={{ width: '20px', height: '20px' }} />
  },
  'bill-payments': {
    id: 'bill-payments',
    path: '/bill-payment-overlay',
    label: 'Bill Payments',
    navLabel: 'Bills',
    icon: () => <Receipt style={{ width: '20px', height: '20px' }} />
  },
  'vouchers': {
    id: 'vouchers',
    path: '/vouchers',
    label: 'My Vouchers',
    navLabel: 'Vouchers',
    icon: () => <Ticket style={{ width: '20px', height: '20px' }} />
  },
  'watch-to-earn': {
    id: 'watch-to-earn',
    path: '/transact?service=watch-to-earn',
    label: 'Watch to Earn',
    navLabel: 'Watch',
    icon: () => <Play style={{ width: '20px', height: '20px' }} />
  },
  'loyalty': {
    id: 'loyalty',
    path: '/loyalty-promotions',
    label: 'Rewards Program',
    navLabel: 'Rewards',
    icon: () => <Star style={{ width: '20px', height: '20px' }} />
  },
  'promotions': {
    id: 'promotions',
    path: '/loyalty-promotions',
    label: 'Promotions',
    navLabel: 'Promotions',
    icon: () => <Tag style={{ width: '20px', height: '20px' }} />
  },
  // New Cash-out Services
  'flash-eezicash': {
    id: 'flash-eezicash',
    path: '/flash-eezicash-overlay',
    label: 'Withdraw Cash',
    navLabel: 'Withdraw',
    icon: () => <DollarSign style={{ width: '20px', height: '20px' }} />
  },
  'mmcash-retail': {
    id: 'mmcash-retail',
    path: '/mmcash-retail-overlay',
    label: 'Cash-out at Retail',
    navLabel: 'Cash-out',
    icon: () => <Store style={{ width: '20px', height: '20px' }} />
  },
  'withdraw-cash': {
    id: 'withdraw-cash',
    path: '/withdraw-cash-overlay',
    label: 'Withdraw Cash',
    navLabel: 'Withdraw',
    icon: () => <AtSign style={{ width: '20px', height: '20px' }} />
  },
  // Legacy saved quick-access settings may still contain atm-cashsend.
  'atm-cashsend': {
    id: 'withdraw-cash',
    path: '/withdraw-cash-overlay',
    label: 'Withdraw Cash',
    navLabel: 'Withdraw',
    icon: () => <AtSign style={{ width: '20px', height: '20px' }} />
  },
  'tap-to-add-money': {
    id: 'tap-to-add-money',
    path: '/tap-to-add-money',
    label: 'Tap to Add Money',
    navLabel: 'Tap to Add',
    icon: () => <CreditCard style={{ width: '20px', height: '20px' }} />
  }
} as const;

export function getQuickAccessNavLabel(serviceId: string): string | undefined {
  const normalized = normalizeQuickAccessServiceId(serviceId) as keyof typeof serviceMapping;
  const mapped = serviceMapping[normalized];
  return mapped?.navLabel;
}

// Default navigation items (positions 1, 3, 5)
const defaultNavItems: NavItem[] = [
  {
    id: 'home',
    path: '/dashboard',
    label: 'Home',
    icon: () => <Home style={{ width: '20px', height: '20px' }} />
  },
  {
    id: 'transact',
    path: '/transact',
    label: 'Transact',
    icon: () => <Receipt style={{ width: '28px', height: '28px' }} />
  },
  {
    id: 'support',
    path: '/support',
    label: 'Support',
    icon: () => <HelpCircle style={{ width: '20px', height: '20px' }} />
  },
  {
    id: 'feedback',
    path: '/feedback',
    label: 'Feedback',
    icon: () => <MessageSquare style={{ width: '20px', height: '20px' }} />
  }
];

function resolveQuickAccessService(id: string | undefined, fallbackKey: keyof typeof serviceMapping): NavItem {
  const normalized = normalizeQuickAccessServiceId(id || '') as keyof typeof serviceMapping;
  if (normalized && serviceMapping[normalized]) {
    return serviceMapping[normalized];
  }
  return serviceMapping[fallbackKey];
}

export function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [quickAccessServices, setQuickAccessServices] = useState<string[]>(['send-money', 'vouchers']);

  // Fetch user's quick access services
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const token = getToken();
        if (!token) {
          setQuickAccessServices(['send-money', 'vouchers']);
          return;
        }

        const currentPath = location.pathname;
        if (!shouldShowBottomNav(currentPath)) {
          setQuickAccessServices(['send-money', 'vouchers']);
          return;
        }

        const response = await fetch(`${APP_CONFIG.API.baseUrl}/api/v1/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.settings?.quickAccessServices) {
            setQuickAccessServices(
              data.data.settings.quickAccessServices.map((id: string) => normalizeQuickAccessServiceId(id))
            );
          } else {
            setQuickAccessServices(['send-money', 'vouchers']);
          }
        } else {
          setQuickAccessServices(['send-money', 'vouchers']);
        }
      } catch (error) {
        console.error('Failed to fetch user settings:', error);
        setQuickAccessServices(['send-money', 'vouchers']);
      }
    };

    fetchUserSettings();

    const handleSettingsUpdate = () => {
      fetchUserSettings();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  const buildNavItems = (): NavItem[] => {
    const selectedServices = quickAccessServices.slice(0, 2);
    const service1 = resolveQuickAccessService(selectedServices[0], 'airtime-data');
    const service2 = resolveQuickAccessService(selectedServices[1], 'vouchers');

    return [
      defaultNavItems[0],
      service1,
      defaultNavItems[1],
      service2,
      defaultNavItems[2]
    ];
  };

  const navItems = buildNavItems();

  const getActiveTabId = () => {
    const currentPath = location.pathname;
    
    if (currentPath === '/transactions') {
      return 'home';
    }
    
    const activeItem = navItems.find(item => item.path === currentPath);
    return activeItem?.id || 'home';
  };

  const activeTab = getActiveTabId();

  if (!shouldShowBottomNav(location.pathname)) return null;

  return (
    <nav
      aria-label="Main"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '375px',
        minHeight: '80px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '6px 8px',
        paddingBottom: 'calc(6px + env(safe-area-inset-bottom, 0px))',
        fontFamily: 'Montserrat, sans-serif',
      }}
      data-fixed-navigation="true"
    >
      {navItems.map((item) => {
        const isActive = activeTab === item.id;
        const isTransact = item.id === 'transact';
        const tabLabel = item.navLabel || item.label;
        
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.path)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '6px 2px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              minWidth: '0',
              minHeight: '44px',
              flex: isTransact ? '1.2' : '1',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '10px',
              fontWeight: isActive ? '600' : '400',
              color: isActive ? '#86BE41' : '#6B7280',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              borderRadius: '8px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isTransact ? '32px' : '24px',
                height: isTransact ? '32px' : '24px',
                borderRadius: '6px',
                backgroundColor: isActive ? '#86BE41' : 'transparent',
                flexShrink: 0,
              }}
            >
              <div style={{
                color: isActive ? '#FFFFFF' : '#6B7280',
              }}>
                {item.icon()}
              </div>
              
              {isActive && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: '#86BE41',
                    border: '2px solid #FFFFFF'
                  }}
                />
              )}
            </div>
            
            <span
              style={{
                fontSize: '10px',
                fontWeight: isActive ? '600' : '400',
                color: isActive ? '#86BE41' : '#6B7280',
                textAlign: 'center',
                lineHeight: 1.15,
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {tabLabel}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
